import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fabric } from 'fabric';
import styled from 'styled-components';
import { loadFonts, getFontFamily } from '../utils/fontLoader';
import EmojiPicker from './EmojiPicker';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

const ArtboardContainer = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const FloatingEmojiButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  background: #4285f4;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.2s;

  &:hover {
    background: #3367d6;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SaveButton = styled.button`
  position: fixed;
  top: 20px;
  right: 80px;
  padding: 8px 16px;
  background: #10b981;
  border: none;
  border-radius: 20px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.2s;

  &:hover {
    background: #059669;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
  }
`;


const ArtboardWrapper = styled.div<{ $imageMode?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .canvas-container {
    ${props => props.$imageMode ? `
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      transform: none !important;
    ` : `
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
      border-radius: 8px;
      position: absolute !important;
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      max-width: 100% !important;
      max-height: 100% !important;
    `}
  }

  .canvas-container canvas {
    width: 100% !important;
    height: 100% !important;
  }

  .lower-canvas, .upper-canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

interface PublicArtboardViewProps {
  artboardId?: string;
}

const PublicArtboardView: React.FC<PublicArtboardViewProps> = ({ artboardId }) => {
  const { id } = useParams<{ id: string }>();
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [artboardData, setArtboardData] = useState<any>(null);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const isGeneratingRef = useRef(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [emojiCount, setEmojiCount] = useState(0);
  const isImageMode = new URLSearchParams(window.location.search).get('image') === 'true';
  const isLoadingFromJSON = useRef(false);

  // Load all fonts when component mounts
  useEffect(() => {
    const initializeFonts = async () => {
        try {
        loadFonts();
          setFontsLoaded(true);
        } catch (error) {
        console.warn('Font initialization failed:', error);
          setFontsLoaded(true); // Continue anyway
      }
    };

    initializeFonts();
  }, []);

  // Function to save artboard changes
  const saveArtboard = async () => {
    if (!canvas || !artboardData || isSaving) return;

    setIsSaving(true);
    try {
      // Get canvas data
      const canvasData = canvas.toJSON();
      // Generate preview thumbnail
      const preview = await generateImage();
      if (!preview) {
        throw new Error('Failed to generate preview');
      }

      // Update the artboard data with new canvas data
      const updatedArtboardData = {
        ...artboardData,
        ...canvasData,
        objects: canvasData.objects
      };

      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        // Don't save, just update local state
        setArtboardData(updatedArtboardData);
        return;
      }

      // Save to backend
      const response = await fetch(`${API_URL}/api/artboards/${id || artboardId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: artboardData.name || 'Untitled Artboard',
          data: updatedArtboardData,
          preview: preview
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save artboard');
      }

      // Update local artboard data
      setArtboardData(updatedArtboardData);
    } catch (error) {
      console.error('Error saving artboard:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to generate image from canvas
  const generateImage = async () => {
    if (!canvas) {
      console.error('No canvas available for image generation');
      return null;
    }
    
    // Store current zoom level
    const currentZoom = canvas.getZoom();

    // Reset zoom to 1 for accurate image generation
    canvas.setZoom(1);

    // Ensure all objects are visible and properly rendered
    const objects = canvas.getObjects();
    objects.forEach(obj => {
      obj.set({
        selectable: false,
        hasControls: false,
        hasBorders: false
      });
    });

    // Force a complete render
    canvas.renderAll();

    // Generate image data URL with a slight delay to ensure rendering is complete
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        try {
          // Use Fabric.js's toDataURL method with consistent scaling
          const dataUrl = canvas.toDataURL({
            format: 'png',
            quality: 1,
            multiplier: 1, // Use 1 to match editor view
            enableRetinaScaling: false
          });
          
          // Restore zoom level
          canvas.setZoom(currentZoom);
          
          // Restore object properties
          objects.forEach(obj => {
            obj.set({
              selectable: false,
              hasControls: false,
              hasBorders: false
            });
          });
          
          canvas.renderAll();
          
          resolve(dataUrl);
        } catch (error) {
          console.error('Error generating image:', error);
          // Restore zoom level even on error
          canvas.setZoom(currentZoom);
          canvas.renderAll();
          resolve('');
        }
      }, 1000); // Increased delay to ensure rendering is complete
    });
  };



  // Effect to handle image generation
  useEffect(() => {
    if (!canvas) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const imageParam = params.get('image');
    const shouldGenerateImage = imageParam === 'true';

    // Generate image if requested
    if (shouldGenerateImage && !isGeneratingRef.current) {
      isGeneratingRef.current = true;

      // Small delay to ensure canvas is fully loaded
      setTimeout(() => {
        generateImage().then(dataUrl => {
          if (dataUrl) {
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `artboard-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Remove the image parameter from URL to prevent re-triggering
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('image');
            window.history.replaceState({}, '', newUrl.toString());
          } else {
            console.error('Failed to generate image: empty data URL');
          }
          isGeneratingRef.current = false;
        });
      }, 1000); // Delay to ensure canvas is fully loaded
    }
  }, [canvas, window.location.search]);

  // Handle newly added objects (like emojis) in public view
  useEffect(() => {
    if (!canvas) return;

    const handleObjectAdded = (e: any) => {
      // Skip processing during loadFromJSON to avoid interference
      if (isLoadingFromJSON.current) return;

      const obj = e.target;
      if (obj) {
        // Set properties for public view (non-interactive but visible)
        obj.set({
          selectable: false,
          hasControls: false,
          hasBorders: false,
          evented: false,
          visible: true,
          opacity: 1
        });
        
        // Force the object to recalculate its transform matrix
        obj.setCoords();
        
        // Ensure the object is properly positioned and scaled
        if (obj.type === 'image' && obj.name === 'emoji') {
          // For emoji images, ensure they're properly sized and positioned
          const emojiSize = 100;
          const scale = emojiSize / Math.max(obj.width || 1, obj.height || 1);
          
          // Keep the original position if it's already set, otherwise center it
          const currentLeft = obj.left || (canvas.width! / 2) - (emojiSize / 2);
          const currentTop = obj.top || (canvas.height! / 2) - (emojiSize / 2);
          
          obj.set({
            scaleX: scale,
            scaleY: scale,
            left: currentLeft,
            top: currentTop,
            originX: 'left',
            originY: 'top'
          });
          
        }
        
        // Render the canvas to show the new object
        canvas.renderAll();

        // Update emoji count
        const emojis = canvas.getObjects().filter(obj => obj.name === 'emoji');
        setEmojiCount(emojis.length);
        
        // Force multiple renders to ensure visibility
        setTimeout(() => {
          canvas.renderAll();
        }, 100);

        setTimeout(() => {
          canvas.renderAll();
        }, 500);
      }
    };

    canvas.on('object:added', handleObjectAdded);

    return () => {
      canvas.off('object:added', handleObjectAdded);
    };
  }, [canvas]);

  // Initialize canvas
  useEffect(() => {
    if (!canvas && canvasRef.current && containerRef.current && artboardData && fontsLoaded) {
      const container = containerRef.current;
      const containerWidth = container.getBoundingClientRect().width;
      const containerHeight = container.getBoundingClientRect().height;

      const fabricCanvas = new fabric.Canvas(canvasRef.current, {
        width: artboardData.width,
        height: artboardData.height,
        backgroundColor: artboardData.backgroundColor || '#ffffff',
        preserveObjectStacking: true,
      });

      // Calculate scale to fit the artboard within the container while maintaining aspect ratio
      // and respecting the maximum dimensions (matching main Artboard component logic)
      let scale: number;
      if (isImageMode) {
        // In image mode (Puppeteer), use zoom 1 for pixel-perfect rendering
        scale = 1;
      } else {
        const maxWidth = Math.min(artboardData.width, containerWidth * 0.9);
        const maxHeight = Math.min(artboardData.height, containerHeight * 0.9);
        const scaleX = maxWidth / artboardData.width;
        const scaleY = maxHeight / artboardData.height;
        scale = Math.min(scaleX, scaleY);
      }

      // Set initial zoom to fit the artboard
      fabricCanvas.setZoom(scale);
      
      // Update canvas container dimensions to match the scaled canvas size
      const canvasContainer = (fabricCanvas as any).wrapperEl as HTMLElement;
      if (canvasContainer) {
        if (isImageMode) {
          canvasContainer.style.width = `${artboardData.width}px`;
          canvasContainer.style.height = `${artboardData.height}px`;
        } else {
          canvasContainer.style.width = `800px`;
          canvasContainer.style.height = `600px`;
        }
      }
      
      fabricCanvas.renderAll();
      setCanvas(fabricCanvas);

      // Handle window resize to maintain proper scaling
      const handleResize = () => {
        const containerWidth = container.getBoundingClientRect().width;
        const containerHeight = container.getBoundingClientRect().height;
        
        // Calculate scale while maintaining aspect ratio and respecting max dimensions
        const maxWidth = Math.min(artboardData.width, containerWidth * 0.9);
        const maxHeight = Math.min(artboardData.height, containerHeight * 0.9);
        const scaleX = maxWidth / artboardData.width;
        const scaleY = maxHeight / artboardData.height;
        const scale = Math.min(scaleX, scaleY);
        
        // Store current objects and their states
        const objects = fabricCanvas.getObjects();
        const objectStates = objects.map(obj => ({
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
          skewX: obj.skewX,
          skewY: obj.skewY
        }));
        
        // Update canvas zoom
        fabricCanvas.setZoom(scale);
        
        // Update canvas container dimensions
        const canvasContainer = (fabricCanvas as any).wrapperEl as HTMLElement;
        if (canvasContainer) {
          const scaledWidth = Math.min(artboardData.width * scale, maxWidth);
          const scaledHeight = Math.min(artboardData.height * scale, maxHeight);
          canvasContainer.style.width = `${scaledWidth}px`;
          canvasContainer.style.height = `${scaledHeight}px`;
        }
        
        // Restore object positions and transformations
        objects.forEach((obj, index) => {
          const state = objectStates[index];
          obj.set({
            left: state.left,
            top: state.top,
            scaleX: state.scaleX,
            scaleY: state.scaleY,
            angle: state.angle,
            skewX: state.skewX,
            skewY: state.skewY
          });
        });
        
        fabricCanvas.renderAll();
      };

      window.addEventListener('resize', handleResize);

      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
        fabricCanvas.dispose();
      };
    }
  }, [canvas, artboardData, fontsLoaded]);

  // Cleanup canvas on unmount
  useEffect(() => {
    return () => {
      if (canvas) {
        canvas.dispose();
      }
    };
  }, [canvas]);

  // Load artboard data
  useEffect(() => {
    const loadArtboard = async () => {
      try {
        const response = await fetch(`${API_URL}/api/artboards/public/${id || artboardId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch artboard');
        }

        const data = await response.json();

        if (!data.data) {
          throw new Error('No artboard data found');
        }

        setArtboardData(data.data);
      } catch (error) {
        console.error('Error loading public artboard:', error);
      }
    };

    loadArtboard();
  }, [id, artboardId]);

  // Load canvas data and update shortcodes
  useEffect(() => {
    const loadCanvasData = async () => {
      if (canvas && artboardData && fontsLoaded) {
        
        // Check for shortcodes in URL parameters before loading canvas data
        const params = new URLSearchParams(window.location.search);

        // Check for both old and new shortcode keys for backward compatibility
        const hasShortcodes = Array.from(params.keys()).some(key => 
          ['firstName', 'FirstName', 'lastName', 'LastName', 'email', 'Email', 'companyName', 'CompanyName', 'Country', 'Title', 'date', 'Date', 'time', 'Time', 
           'name', 'company', 'phone', 'address', 'website'].includes(key)
        );

        // If shortcodes exist, create a modified version of the artboard data
        let modifiedArtboardData = artboardData;
        if (hasShortcodes) {
          modifiedArtboardData = JSON.parse(JSON.stringify(artboardData)); // Deep clone

          // Update shortcodes in the cloned data
          if (modifiedArtboardData.objects) {
            let shortcodeReplacements = 0;
            modifiedArtboardData.objects.forEach((obj: any, _index: number) => {
              if ((obj.type === 'i-text' || obj.type === 'textbox') && obj.text) {
                let text = obj.text;
                const shortcodes = [
                  // New shortcode format
                  { key: 'firstName', value: '{{firstName}}' },
                  { key: 'FirstName', value: '{{FirstName}}' },
                  { key: 'lastName', value: '{{lastName}}' },
                  { key: 'LastName', value: '{{LastName}}' },
                  { key: 'email', value: '{{email}}' },
                  { key: 'Email', value: '{{Email}}' },
                  { key: 'companyName', value: '{{companyName}}' },
                  { key: 'CompanyName', value: '{{CompanyName}}' },
                  { key: 'Country', value: '{{Country}}' },
                  { key: 'Title', value: '{{Title}}' },
                  { key: 'date', value: '{{date}}' },
                  { key: 'Date', value: '{{Date}}' },
                  { key: 'time', value: '{{time}}' },
                  { key: 'Time', value: '{{Time}}' },
                  // Old shortcode format for backward compatibility
                  { key: 'name', value: '{{name}}' },
                  { key: 'company', value: '{{company}}' },
                  { key: 'phone', value: '{{phone}}' },
                  { key: 'address', value: '{{address}}' },
                  { key: 'website', value: '{{website}}' }
                ];
                
                // Check if the text contains any shortcodes
                const containsShortcodes = shortcodes.some(({ value }) => text.includes(value));
                
                if (containsShortcodes) {
                  // Check if ALL shortcodes in the text should be hidden
                  const shouldHideObject = shortcodes.every(({ key, value }) => {
                    if (text.includes(value)) {
                      // If this shortcode is in the text, check if it should be hidden
                      if (!params.has(key)) {
                        return true; // Hide if parameter doesn't exist
                      }
                      const paramValue = params.get(key);
                      // Hide if parameter is empty, blank, or contains the shortcode pattern itself
                      return !paramValue || paramValue.trim() === '' || paramValue === value;
                    }
                    return true; // If shortcode not in text, consider it "hidden" (doesn't affect visibility)
                  });

                  if (shouldHideObject) {
                    // Hide the entire object if all shortcodes in the text should be hidden
                    obj.visible = false;
                    return;
                  } else {
                    // Show the object if at least one shortcode has a valid parameter
                    obj.visible = true;

                    // Process shortcode replacements for objects that should be visible
                    shortcodes.forEach(({ key, value }) => {
                      if (params.has(key) && text.includes(value)) {
                        const paramValue = params.get(key) || '';

                        // Only process if the parameter has a non-empty value and is not the shortcode pattern itself
                        if (paramValue && paramValue.trim() !== '' && paramValue !== value) {
                          // If the parameter value contains the shortcode pattern (like {{firstName}}), replace with "Hi there"
                          let replacementValue;
                          if (paramValue.includes(value)) {
                            replacementValue = 'Hi there';
                          } else {
                            replacementValue = paramValue;
                          }
                          
                          const regex = new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                          text = text.replace(regex, replacementValue);
                          shortcodeReplacements++;
                        }
                      }
                    });
                    
                    if (text !== obj.text) {
                      obj.text = text;
                    }
                  }
                } else {
                  // If text doesn't contain shortcodes but is empty or only whitespace, hide it
                  if (!text || text.trim() === '') {
                    obj.visible = false;
                    return;
                  }
                }

                // Additional check: if text is very short after processing, hide it
                if (text && text.trim().length <= 3) {
                  obj.visible = false;
                  return;
                }
              }
            });
          }
        }
        
        // Load the modified canvas data
        isLoadingFromJSON.current = true;
        canvas.loadFromJSON(modifiedArtboardData, async () => {
          isLoadingFromJSON.current = false;
          
          // Process all objects
          const processObjects = async () => {
            const objects = canvas.getObjects();

            // First pass: Handle SVG groups that need to be reloaded
            const svgPromises: Promise<void>[] = [];
            for (let i = 0; i < objects.length; i++) {
              const obj = objects[i];
              if (obj.type === 'group' && (obj as any).svgData) {
                const svgData = (obj as any).svgData;
                const objProps = {
                  left: obj.left,
                  top: obj.top,
                  scaleX: obj.scaleX,
                  scaleY: obj.scaleY,
                  angle: obj.angle,
                  skewX: obj.skewX,
                  skewY: obj.skewY,
                  flipX: obj.flipX,
                  flipY: obj.flipY,
                  opacity: obj.opacity
                };
                
                svgPromises.push(new Promise<void>((resolve) => {
                  fabric.loadSVGFromString(svgData, (svgObjects, options) => {
                    if (svgObjects && svgObjects.length > 0) {
                      const svgGroup = fabric.util.groupSVGElements(svgObjects, options);
                      svgGroup.set({
                        ...objProps,
                        selectable: false,
                        hasControls: false,
                        hasBorders: false
                      });
                      (svgGroup as any).svgData = svgData;
                      
                      // Replace the old group with the properly loaded SVG
                      canvas.remove(obj);
                      canvas.insertAt(svgGroup, i, false);
                    }
                    resolve();
                  });
                }));
              }
            }
            
            // Wait for all SVGs to be reloaded
            if (svgPromises.length > 0) {
              await Promise.all(svgPromises);
            }
            
            // Refresh the objects list after SVG replacements
            const updatedObjects = canvas.getObjects();
            
            for (const obj of updatedObjects) {
              // Emojis are now stored as base64 data (like uploaded images)
              // No special handling needed - they will load directly from the dataUrl
              
              // Set object properties for public view (non-interactive)
              obj.set({
                selectable: false,
                hasControls: false,
                hasBorders: false
              });
              
              // Handle text objects
              if (obj.type === 'i-text' || obj.type === 'textbox') {
                const textObj = obj as fabric.IText;
                
                const originalFontFamily = textObj.fontFamily || 'Inter';
                const originalFontWeight = textObj.fontWeight;

                const currentFontFamily = getFontFamily(originalFontFamily);
                
                // Normalize font weight to supported weights (matching server logic)
                let currentFontWeight = typeof originalFontWeight === 'string' 
                  ? parseInt(originalFontWeight, 10) 
                  : (originalFontWeight as number) || 400;
                if (currentFontWeight < 300) currentFontWeight = 300;
                if (currentFontWeight > 700) currentFontWeight = 700;
                if (currentFontWeight === 500) currentFontWeight = 600; // Map medium to semibold
                
                // Ensure the font is loaded before updating the text object
                if ('fonts' in document) {
                  try {
                    // Load the specific font weight
                    await document.fonts.load(`${currentFontWeight} 16px ${currentFontFamily}`);
                    
                    // Verify the font is actually available
                    const isFontLoaded = document.fonts.check(`16px ${currentFontFamily}`);
                    if (!isFontLoaded) {
                      console.warn(`Font ${currentFontFamily} not available after loading, using fallback`);
                    }
                  } catch (error) {
                    console.warn(`Failed to load font for text object: ${currentFontWeight} ${currentFontFamily}`, error);
                  }
                }
                
                // Update the text object while preserving original sizing
                textObj.set({
                  fontFamily: currentFontFamily,
                  fontWeight: currentFontWeight,
                  selectable: false,
                  hasControls: false,
                  hasBorders: false
                });

                // Force the object to recalculate its transform matrix
                textObj.setCoords();
              } else {
                // Force the object to recalculate its transform matrix
                obj.setCoords();
              }
            }
          }
          
          await processObjects();

          // Ensure all shape objects are visible and properly configured
          const allObjects = canvas.getObjects();
          allObjects.forEach((obj, _idx) => {
            // Ensure all objects are visible
            if (obj.visible === false && obj.type !== 'i-text' && obj.type !== 'textbox') {
              // Only text objects should be hidden (by shortcode logic)
              obj.set({ visible: true });
            }

            // Recalculate coords for all objects
            obj.setCoords();
          });

          // Force a render to ensure all properties are applied
          canvas.renderAll();
          
          // Update emoji count after loading
          const loadedObjects = canvas.getObjects();
          const emojis = loadedObjects.filter(obj => obj.name === 'emoji');
          setEmojiCount(emojis.length);
          
          // Force multiple renders to ensure fonts are applied properly
          setTimeout(() => {
            canvas.renderAll();
          }, 100);
          
          setTimeout(() => {
            canvas.renderAll();
          }, 500);
          
          // Additional render after a longer delay to catch any late font loading
          setTimeout(() => {
            canvas.renderAll();
          }, 1000);
        });
      }
    };
    
    loadCanvasData();
  }, [canvas, artboardData, fontsLoaded]);

  return (
    <ArtboardContainer>
      <ArtboardWrapper ref={containerRef} $imageMode={isImageMode}>
        {!fontsLoaded && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#666',
            fontSize: '16px'
          }}>
            Loading fonts...
          </div>
        )}
        <canvas ref={canvasRef} />
      </ArtboardWrapper>
      
      {!isImageMode && (
        <>
          {/* Save Button */}
          <SaveButton
            onClick={saveArtboard}
            disabled={isSaving}
            title="Save Changes"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </SaveButton>

          {/* Floating Emoji Button */}
          <FloatingEmojiButton
            ref={emojiButtonRef}
            onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
            title={`Add Emoji (${emojiCount} emojis)`}
          >
            😀
            {emojiCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                background: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {emojiCount}
              </span>
            )}
          </FloatingEmojiButton>

          {/* Emoji Picker */}
          <EmojiPicker
            isOpen={isEmojiPickerOpen}
            onClose={() => setIsEmojiPickerOpen(false)}
            triggerRef={emojiButtonRef}
            canvas={canvas}
            onEmojiAdded={saveArtboard}
          />
        </>
      )}
    </ArtboardContainer>
  );
};

export default PublicArtboardView; 